<template>
    <div class="component dialogue-slider section" data-shape-id="dialogue">
        <particles :particles="particles" ref="particles" />
        <div class="slider-dialogue" ref="slider" v-touch:swipe.left="swipeHandler" v-touch:swipe.right="swipeHandler">
            <div class="slider-dialogue-slide" :class="{ active: activeSlide === 1 }">
                <div class="section-opener">
                    <div class="wrapper wrapper-small">
                        <div class="overline">Matching Perspectives</div>
                        <h2 class="major-heading">
                            User and<br />
                            Brand
                        </h2>
                        <p>
                            Each interaction has at least two sides: The user and the brand. Both have their own needs
                            and goals in mind that need to be matched. What are they?
                        </p>
                    </div>
                </div>
            </div>
            <div class="slider-dialogue-slide" :class="{ active: activeSlide === 2 }">
                <div class="wrapper wrapper-chat">
                    <div class="message message-client">
                        <div class="sender-image">
                            <img src="../../../assets/svg/avatar/lady-01.svg" />
                        </div>
                        <div class="message-copy">
                            <div class="sender-name">User:</div>
                            <p class="sender-message">
                                I want to have fast access, anywhere, anytime. Services need to be easy to use.
                            </p>
                        </div>
                    </div>
                    <div class="message message-telekom">
                        <div class="sender-image">
                            <img src="../../../assets/svg/logo/dt-logo.svg" />
                        </div>
                        <div class="message-copy">
                            <div class="sender-name">Brand:</div>
                            <p class="sender-message">
                                We want to deliver the best network experience together with products and services that
                                enrich everyday life.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="slider-dialogue-slide" :class="{ active: activeSlide === 3 }">
                <div class="wrapper wrapper-chat">
                    <div class="message message-client">
                        <div class="sender-image">
                            <img src="../../../assets/svg/avatar/guy-01.svg" />
                        </div>
                        <div class="message-copy">
                            <div class="sender-name">User:</div>
                            <p class="sender-message">
                                I want to be close to my family and friends.
                            </p>
                        </div>
                    </div>
                    <div class="message message-telekom">
                        <div class="sender-image">
                            <img src="../../../assets/svg/logo/dt-logo.svg" />
                        </div>
                        <div class="message-copy">
                            <div class="sender-name">Brand:</div>
                            <p class="sender-message">
                                We want to enable participation and let customers experience “Life is for Sharing”.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="slider-dialogue-slide" :class="{ active: activeSlide === 4 }">
                <div class="wrapper wrapper-chat">
                    <div class="message message-client">
                        <div class="sender-image">
                            <img src="../../../assets/svg/avatar/lady-02.svg" />
                        </div>
                        <div class="message-copy">
                            <div class="sender-name">User:</div>
                            <p class="sender-message">
                                I want to feel safe online and know that my data is well protected.
                            </p>
                        </div>
                    </div>
                    <div class="message message-telekom">
                        <div class="sender-image">
                            <img src="../../../assets/svg/logo/dt-logo.svg" />
                        </div>
                        <div class="message-copy">
                            <div class="sender-name">Brand:</div>
                            <p class="sender-message">
                                We want to be a trusted companion in the digital world.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="slider-dialogue-slide" :class="{ active: activeSlide === 5 }">
                <div class="wrapper wrapper-chat">
                    <div class="message message-client">
                        <div class="sender-image">
                            <img src="../../../assets/svg/avatar/guy-02.svg" />
                        </div>
                        <div class="message-copy">
                            <div class="sender-name">User:</div>
                            <p class="sender-message">
                                I want to be recognized as a loyal customer. Always bear in mind my history with you.
                            </p>
                        </div>
                    </div>
                    <div class="message message-telekom">
                        <div class="sender-image">
                            <img src="../../../assets/svg/logo/dt-logo.svg" />
                        </div>
                        <div class="message-copy">
                            <div class="sender-name">Brand:</div>
                            <p class="sender-message">
                                We want to have longlasting relationships with our customers.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="slider-dots-container">
                <div class="slider-dots" :class="{ 'color-scheme-inverted': activeSlide === 1 }">
                    <div class="dot" @click="goToSlide(1)" :class="{ active: activeSlide === 1 }"></div>
                    <div class="dot" @click="goToSlide(2)" :class="{ active: activeSlide === 2 }"></div>
                    <div class="dot" @click="goToSlide(3)" :class="{ active: activeSlide === 3 }"></div>
                    <div class="dot" @click="goToSlide(4)" :class="{ active: activeSlide === 4 }"></div>
                    <div class="dot" @click="goToSlide(5)" :class="{ active: activeSlide === 5 }"></div>
                </div>
            </div>
            <div
                v-show="!hasTouchSupport"
                data-cursor-state="arrow-left"
                class="custom-cursor-trigger-zone cc-arrow-left"
                :class="{ 'is-hidden': activeSlide === 1, 'has-full-width': activeSlide === 5 }"
                @click="goToPreviousSlide"
            ></div>
            <div
                v-show="!hasTouchSupport"
                data-cursor-state="arrow-right"
                class="custom-cursor-trigger-zone cc-arrow-right"
                :class="{ 'is-hidden': activeSlide === 5, 'has-full-width': activeSlide === 1 }"
                @click="goToNextSlide"
            ></div>
        </div>
    </div>
</template>

<script>
    import Particles from '../../Particles.vue';

    export default {
        name: 'DialogueSlider',
        components: { Particles },
        data() {
            return {
                activeSlide: 1,
                particles: [
                    {
                        shape: 'circle',
                        color: 'magenta',
                        size: 'l',
                        top: 30,
                        right: 5,
                    },
                    {
                        shape: 'parallelogram',
                        color: 'catskill-white',
                        top: 2,
                        left: 70,
                    },
                    {
                        shape: 'parallelogram',
                        color: 'catskill-white',
                        top: 76,
                        size: 'l',
                        left: 5,
                    },
                ],
            };
        },
        methods: {
            goToSlide(id) {
                this.handleSlideChange(id);
            },
            handleSlideChange(id) {
                let activeSlide = document.querySelector('.slider-dialogue-slide.active');
                activeSlide.classList.add('on-leave');

                setTimeout(() => {
                    activeSlide.classList.remove('on-leave');
                    this.activeSlide = id;
                    if (this.activeSlide === 1) {
                        this.$store.dispatch('updateBackgroundShape', `dialogue`);
                        this.$refs.particles.resetParticleColor();
                    } else {
                        this.$store.dispatch('updateBackgroundShape', `dialogueSlide0${this.activeSlide}`);
                        this.$refs.particles.forceParticleColor('magenta');
                    }
                }, 200);
            },
            goToNextSlide() {
                if (this.activeSlide < 5) {
                    let nextSlide = this.activeSlide + 1;
                    this.handleSlideChange(nextSlide);
                }
            },
            goToPreviousSlide() {
                if (this.activeSlide > 1) {
                    let previousSlide = this.activeSlide - 1;
                    this.handleSlideChange(previousSlide);
                }
            },
            swipeHandler(direction) {
                if (direction === 'left') {
                    this.goToNextSlide();
                }

                if (direction === 'right') {
                    this.goToPreviousSlide();
                }
            },
            resetSlider() {
                this.activeSlide = 1;
                this.$refs.particles.resetParticleColor();
            },
        },
        computed: {
            hasTouchSupport() {
                return this.$store.getters.hasTouchSupport;
            },
        },
    };
</script>

<style scoped lang="scss">
    .component {
        position: relative;
        width: 100%;
    }

    .section-opener {
        color: #ffffff;
        margin: 0 auto;
    }

    .shape {
        display: none;
        position: absolute;
        width: 80vw;
        left: 10vw;
        top: 0;
    }

    .slider-dialogue {
        width: 100%;
        height: 80vh;
        position: relative;
        @include flex($justify-content: center);

        @media (max-width: breakpoint('medium')) {
            height: 90vh;
        }

        .slider-dialogue-slide {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: none;

            .message-client {
                @media (max-width: breakpoint('medium')) {
                    margin-bottom: spacing(5);
                }
            }

            &.on-leave {
                .message-client,
                .message-telekom {
                    animation: anim-fade-out 0.3s 0s !important;
                }

                @keyframes anim-fade-out {
                    from {
                        transform: translateY(0px);
                        opacity: 1;
                    }

                    to {
                        transform: translateY(15px);
                        opacity: 0;
                    }
                }
            }

            &.active {
                @include flex($align-items: center);

                .message-client {
                    animation: anim-fade-in 0.75s 0.1s forwards;
                }

                .message-telekom {
                    animation: anim-fade-in 0.75s 0.25s forwards;
                }

                @keyframes anim-fade-in {
                    from {
                        transform: translateY(15px);
                        opacity: 0;
                    }

                    to {
                        transform: translateY(0px);
                        opacity: 1;
                    }
                }
            }

            &:nth-of-type(2) {
                .message-client {
                    .sender-image,
                    .message-copy {
                        background-color: color('wedgewood');
                    }

                    .message-copy:after {
                        border-left-color: color('wedgewood');
                    }
                }

                .message .message-copy .sender-name {
                    display: block;
                }
            }

            &:nth-of-type(3) {
                .message-client {
                    .sender-image,
                    .message-copy {
                        background-color: color('wild-blue-yonder');
                    }
                    .message-copy:after {
                        border-left-color: color('wild-blue-yonder');
                    }
                }
            }

            &:nth-of-type(4) {
                .message-client {
                    .sender-image,
                    .message-copy {
                        background-color: color('half-baked');
                    }
                    .message-copy:after {
                        border-left-color: color('half-baked');
                    }
                }
            }

            &:nth-of-type(5) {
                .message-client {
                    .sender-image,
                    .message-copy {
                        background-color: color('wedgewood');
                    }
                    .message-copy:after {
                        border-left-color: color('wedgewood');
                    }
                }
            }
        }

        .slider-dots-container {
            position: absolute;
            width: 100%;
            bottom: 20px;
            left: 0;
            z-index: 1;
            @include flex($justify-content: center);
        }

        .slider-dots {
            .dot {
                display: inline-block;
                width: 7px;
                height: 7px;
                border-radius: 50%;
                border: 1px solid #000;
                cursor: pointer;
                margin: 0 15px;

                &.active {
                    border-color: color('magenta');
                    background-color: color('magenta');
                }
            }

            &.color-scheme-inverted {
                .dot {
                    border: 1px solid #ffffff;

                    &.active {
                        border-color: #ffffff;
                        background-color: #ffffff;
                    }
                }
            }
        }

        .custom-cursor-trigger-zone {
            position: absolute;
            height: 100%;
            width: 50%;

            &.is-hidden {
                width: 0;
            }

            &.cc-arrow-left {
                left: 0;
            }

            &.cc-arrow-right {
                right: 0;
            }

            &.has-full-width {
                width: 100%;
            }
        }

        .wrapper-chat {
            width: 600px;

            @media (max-width: 660px) {
                width: calc(100% - 60px);
            }
        }

        .slide-illustration {
            opacity: 0;
        }

        .message {
            @include flex($align-items: flex-end);
            opacity: 0;

            .sender-image,
            .message-copy {
                border-radius: 6px;
                box-shadow: 0px 20px 20px 0px rgba(0, 0, 0, 0.25);
            }

            .sender-image {
                position: relative;
                width: 100px;
                height: 100px;

                img {
                    @include reset-img();
                    border-radius: 6px;
                }

                @media (max-width: 660px) {
                    width: 60px;
                    height: 60px;
                }

                @media (max-width: breakpoint('small')) {
                    display: none !important;
                }
            }

            .message-copy {
                width: 250px;
                padding: 30px;
                color: #ffffff;
                margin: 0px spacing(3) 80px spacing(3);
                position: relative;

                @media (max-width: breakpoint('small')) {
                    width: calc(100% - 100px);
                    margin: 0;
                    padding: 20px;
                }

                &:after {
                    position: absolute;
                    bottom: -30px;
                    left: 0;
                    content: '';
                    width: 0;
                    height: 0;
                    border-top: 30px solid transparent;
                    border-left: 30px solid color('wedgewood');
                    border-bottom: 30px solid transparent;

                    @media (max-width: breakpoint('small')) {
                        border-width: 20px !important;
                        bottom: -20px;
                    }
                }

                .sender-name {
                    text-transform: uppercase;
                    font-weight: font-weight('extra-bold');
                    margin-bottom: spacing(1);
                    font-size: 18px;
                    display: none;

                    @media (max-width: breakpoint('small')) {
                        display: block;
                    }
                }

                .sender-message {
                    font-size: 16px;
                }
            }

            &-telekom {
                flex-direction: row-reverse;

                .sender-image,
                .message-copy {
                    background-color: color('magenta');
                }

                .sender-image {
                    img {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        width: 32%;
                        border-radius: 0px;
                    }
                }

                .message-copy:after {
                    border-left: 0px;
                    border-right: 30px solid color('magenta');
                    left: auto;
                    right: 0;
                }
            }
        }
    }
</style>
